// Move replay (client only). Pure: navigates the session's buffer of past positions and turns the
// object-id diff between a position and the one before it into replay marks: what changed (glow),
// where things came from (ghosts), counter and hidden-count changes (chips) and a callout. Like
// motion, it uses only what the seat's own observations contained.

import type { TableModel } from '../model/table.ts';
import type { HistoryFrame } from '../session/Session.ts';
import { diff } from './diff.ts';

export interface ReplayStop {
  frame: HistoryFrame;
  /** The position before it (null for the first position seen, which has nothing to compare). */
  prev: HistoryFrame | null;
  /** 1 for the latest move, 2 for the one before, and so on. */
  movesAgo: number;
  older: number | null;
  newer: number | null;
}

/**
 * The replay stop for a revision: the buffered frame at or just before it. Null when none is
 * buffered. Stops step through moves (frames that logged events and have a position before them to
 * compare with); silent revisions are skipped and do not count towards "moves ago".
 */
export function replayStop(history: HistoryFrame[], revision: number): ReplayStop | null {
  let i = -1;
  for (let k = 0; k < history.length; k++) if (history[k]!.revision <= revision) i = k;
  if (i < 0) return null;
  const moves = history.map((f, k) => (k > 0 && f.events.length > 0 ? k : -1)).filter(k => k > 0);
  const before = moves.filter(k => k < i), after = moves.filter(k => k > i);
  return {
    frame: history[i]!, prev: i > 0 ? history[i - 1]! : null,
    movesAgo: Math.max(1, after.length + (moves.includes(i) ? 1 : 0)),
    older: before.length ? history[before[before.length - 1]!]!.revision : null,
    newer: after.length ? history[after[0]!]!.revision : null,
  };
}

export interface Ghost {
  id: string;
  title: string;
  /** Where it was: on a host object (an occupant), in a zone, or null when nowhere drawn. */
  host: string | null;
  zone: string | null;
}

export interface ReplayMarks {
  /** Objects that moved, arrived or changed owner, plus the hosts they now sit on. */
  changed: Set<string>;
  ghosts: Ghost[];
  counters: { id: string; text: string; gain: boolean }[];
  zones: { id: string; text: string; gain: boolean }[];
  /** The object the callout points at. */
  primary: string | null;
  callout: string;
  /** Objects for choosing the tab to show (changed objects, with occupants counted on their hosts). */
  focus: string[];
}

type Table = Pick<TableModel, 'zones' | 'objects' | 'counters' | 'actor' | 'seats'>;

const signed = (n: number) => (n > 0 ? `+${n}` : `−${-n}`);

export function replayMarks(prev: Table, next: Table, messages: string[]): ReplayMarks {
  const changed = new Set<string>();
  const ghosts: Ghost[] = [];
  const focus: string[] = [];
  const counters: ReplayMarks['counters'] = [];
  const zoneDelta = new Map<string, number>();
  // The callout points at the most telling change: a move between places, else an arrival, else an owner change.
  let primary: { id: string; rank: number } | null = null;
  const hostOf = (t: Table, id: string) => { const o = t.objects.get(id); const h = o?.stack ?? o?.at ?? null; return h && t.objects.has(h) ? h : null; };
  const mark = (id: string, rank: number) => {
    changed.add(id);
    const host = hostOf(next, id);
    if (host) changed.add(host);
    const target = host ?? id;
    focus.push(target);
    if (!primary || rank < primary.rank) primary = { id: target, rank };
  };
  for (const e of diff(prev, next)) {
    switch (e.type) {
      case 'move': {
        // Sliding within the same place (a neighbour left the row) is not a change worth marking.
        if (e.from.zone === e.to.zone && e.from.at === e.to.at && e.from.ref === e.to.ref && e.from.stack === e.to.stack) break;
        mark(e.id, 0);
        const oldHost = hostOf(prev, e.id);
        if (oldHost && oldHost !== hostOf(next, e.id)) ghosts.push({ id: e.id, title: prev.objects.get(e.id)?.title ?? '', host: oldHost, zone: null });
        else if (e.from.zone !== e.to.zone) ghosts.push({ id: e.id, title: prev.objects.get(e.id)?.title ?? '', host: null, zone: e.from.zone });
        break;
      }
      case 'enter': mark(e.id, 1); break;
      case 'exit': {
        const oldHost = hostOf(prev, e.id);
        ghosts.push({ id: e.id, title: prev.objects.get(e.id)?.title ?? '', host: oldHost, zone: oldHost ? null : e.zone });
        break;
      }
      case 'owner': mark(e.id, 2); break;
      case 'counter': if (e.delta !== null && e.delta !== 0) counters.push({ id: e.id, text: signed(e.delta), gain: e.delta > 0 }); break;
      case 'count':
        if (e.from) zoneDelta.set(e.from, (zoneDelta.get(e.from) ?? 0) - e.amount);
        if (e.to) zoneDelta.set(e.to, (zoneDelta.get(e.to) ?? 0) + e.amount);
        break;
      case 'turn': break;
    }
  }
  const zones = [...zoneDelta].filter(([, d]) => d !== 0).map(([id, d]) => ({ id, text: signed(d), gain: d > 0 }));
  const actor = next.seats.find(s => s.id === prev.actor)?.label ?? null;
  const callout = messages.length ? messages.slice(-2).join(' · ') : actor ? `${actor} moved` : 'This changed';
  return { changed, ghosts, counters, zones, primary: (primary as { id: string } | null)?.id ?? null, callout, focus };
}
